import { getRemoteModels, getRemoteParticleDefaults, isLayerEnabled } from '../config/remoteConfig';
import { createDefaultLayerAnimationSettings } from '../config/layerAnimation';
import { useWeatherStore, hasPersistedAnimationSettings } from './weatherStore';
import type { LayerKey, WeatherModel } from './types';

/**
 * يطبّق إعدادات الأدمن على متجر الطقس بعد الترطيب وقبل تركيب الخريطة:
 * - قائمة النماذج المفعّلة (بترتيب الأدمن) مع ضمان صلاحية النموذج المختار.
 * - إطفاء الطبقات المعطَّلة إن كانت ظاهرة افتراضياً.
 */
export function applyRemoteModels(): void {
    const state = useWeatherStore.getState();

    const remote = getRemoteModels();
    if (remote && remote.length > 0) {
        const enabled = remote
            .filter((m) => m.enabled)
            .sort((a, b) => a.sort_order - b.sort_order);
        if (enabled.length > 0) {
            const models: WeatherModel[] = enabled.map((m) => ({
                id: m.model_key as WeatherModel['id'],
                name: m.name,
                resolution: m.resolution_label ?? '',
                provider: m.provider ?? undefined,
                color: m.color ?? undefined,
            }));
            const selectedStillValid = models.some((m) => m.id === state.selectedModel);
            useWeatherStore.setState({
                availableModels: models,
                selectedModel: selectedStillValid ? state.selectedModel : models[0].id,
            });
        }
    }

    /*
     * حارسٌ يعمل **دائماً**، لا داخل الشرط أعلاه فقط.
     *
     * ‏selectedModel محفوظ في localStorage، فمن تركه على نموذج ثمّ أُوقف ذلك النموذج
     * (كـICON في 2026-09-01) يعود إليه عند كل زيارة: تُوجَّه كل الطلبات إلى مجلّد
     * ‏rasters/icon/ المحذوف فيرى خريطةً بلا ألوان ولا شريط زمني، بلا خطأ ظاهر يفسّر
     * ما جرى. والحارس داخل فرع الأدمن لا يكفي: يكفي أن يفشل /api/v1/config (انقطاع
     * أو تباطؤ) حتى تُستعمل القائمة الاحتياطية بلا أي تقويم للاختيار المحفوظ.
     *
     * فنقيس الاختيار إلى القائمة الفعلية أياً كان مصدرها. وهذا يصحّح نفسه تلقائياً
     * حين يعود النموذج: يظهر في القائمة فيبقى اختياره صالحاً.
     */
    const current = useWeatherStore.getState();
    if (current.availableModels.length > 0
        && !current.availableModels.some((m) => m.id === current.selectedModel)) {
        useWeatherStore.setState({ selectedModel: current.availableModels[0].id });
    }

    // الطبقات المعطَّلة من الأدمن لا تبقى ظاهرة (مثل wind/hurricanes الافتراضيتين)
    const visible = { ...useWeatherStore.getState().visibleLayers };
    let changed = false;
    (Object.keys(visible) as LayerKey[]).forEach((key) => {
        if (visible[key] && !isLayerEnabled(key)) {
            visible[key] = false;
            changed = true;
        }
    });
    if (changed) useWeatherStore.setState({ visibleLayers: visible });
}

/**
 * يعيد بناء إعدادات حركة الجسيمات من القيم البعيدة (داشبورد الأدمن) بعد الترطيب.
 *
 * ضروري لأن المتجر يُنشأ لحظة استيراد وحدته — أي قبل أن تنتهي hydrateRemoteConfig()
 * (تُحمّل حزمة الخريطة بالتوازي مع الترطيب) — فتقرأ createDefaultLayerAnimationSettings()
 * قيمَ remoteParticles وهي لا تزال null فتُستخدم ثوابت الكود وتُتجاهل قيم الباك اند.
 * هنا نعيد التوليد بعد أن أصبحت القيم البعيدة متاحة، فتنعكس تعديلات الأدمن على الخريطة.
 *
 * يُستدعى مرة واحدة عند الإقلاع قبل تركيب الخريطة. إن كانت للمستخدم إعدادات جسيمات
 * محفوظة في localStorage فهي مصدر الحقيقة — لا نَدوسها بقيم الأدمن.
 */
export function applyRemoteParticles(): void {
    if (hasPersistedAnimationSettings()) return; // تخصيص المستخدم المحفوظ له الأولوية
    if (!getRemoteParticleDefaults()) return; // لا قيم بعيدة ⇒ نُبقي افتراضيات الكود
    useWeatherStore.setState({ layerAnimationSettings: createDefaultLayerAnimationSettings() });
}
